import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field, inputCls } from "../request/Field";
import { StepContact } from "../request/StepContact";
import { RequestSuccess } from "../request/RequestSuccess";
import { BUDGETS, RESOLUTIONS, USES, EXTRAS, EXAMPLE_BUILDS } from "../../lib/builds";
import { http, errMsg } from "../../lib/api";
import { gbp } from "./PcBuilder";

const Chips = ({ label, options, value, onToggle, multi, testPrefix, error }) => (
  <div>
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
    <div className="mt-2 flex flex-wrap gap-2">
      {options.map((o, i) => {
        const on = multi ? value.includes(o) : value === o;
        return (
          <button key={o} type="button" data-testid={`${testPrefix}-${i}`} onClick={() => onToggle(o)}
            className={`border px-3.5 py-2.5 text-sm transition-[border-color,background-color] duration-300 ${on ? "border-ember bg-ember/10 text-bone" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
            {o}
          </button>
        );
      })}
    </div>
    {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export const BuildForm = ({ f, setF }) => {
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const e = {};
    const hasParts = Object.keys(f.parts || {}).length > 0;
    if (!hasParts && !f.budget) e.budget = "Choose a budget";
    if (!hasParts && !f.resolution) e.resolution = "Choose a target";
    if (f.name.trim().length < 2) e.name = "Enter your name";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email";
    if (f.phone.replace(/\D/g, "").length < 6) e.phone = "Enter a valid phone number";
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please check the highlighted fields");
    setBusy(true);
    try {
      const { partsSummary, ...payload } = f;
      const { data } = await http.post("/builds", payload);
      setDone({ ref: data?.ref, email: f.email });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <RequestSuccess ref_={done.ref} email={done.email} />;
  return (
    <div className="space-y-8" data-testid="build-form">
      {f.partsSummary && (
        <div data-testid="build-form-parts-summary" className="border border-ember/40 bg-ember/[0.06] p-5">
          <div className="flex items-start justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">Your build is attached</p>
            <button type="button" data-testid="build-form-remove-parts" onClick={() => setF((p) => ({ ...p, parts: {}, partsSummary: null }))} className="text-xs text-slate-400 underline hover:text-bone">Remove</button>
          </div>
          <ul className="mt-3 space-y-1 text-sm text-slate-300">{f.partsSummary.lines.map((l) => <li key={l.cat}>{l.name}</li>)}</ul>
          <p className="mt-3 font-display text-xl font-bold">Estimated total {gbp(f.partsSummary.total)}</p>
        </div>
      )}
      {!f.partsSummary && <Chips label="Starting point (optional)" options={EXAMPLE_BUILDS.map((b) => b.name)} value={f.starting_build} onToggle={(v) => set("starting_build", f.starting_build === v ? "" : v)} testPrefix="build-start" />}
      {!f.partsSummary && <Chips label="Budget" options={BUDGETS} value={f.budget} onToggle={(v) => set("budget", v)} testPrefix="build-budget" error={errors.budget} />}
      {!f.partsSummary && <Chips label="Target resolution" options={RESOLUTIONS} value={f.resolution} onToggle={(v) => set("resolution", v)} testPrefix="build-resolution" error={errors.resolution} />}
      <Chips label="What will you use it for?" options={USES} value={f.uses} onToggle={(v) => set("uses", toggle(f.uses, v))} multi testPrefix="build-use" />
      <Field label="Main games (optional)">
        <input data-testid="build-games-input" className={inputCls} value={f.games} onChange={(e) => set("games", e.target.value)} placeholder="e.g. Warzone, Fortnite, EA FC" />
      </Field>
      <Chips label="Also need (optional)" options={EXTRAS} value={f.extras} onToggle={(v) => set("extras", toggle(f.extras, v))} multi testPrefix="build-extra" />
      <Field label="Anything else? (optional)">
        <textarea data-testid="build-notes-input" rows={3} className={`${inputCls} resize-none`} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Colour scheme, RGB or no RGB, size, quiet running…" />
      </Field>
      <div className="border-t border-white/[0.08] pt-8">
        <StepContact f={f} set={set} errors={errors} />
      </div>
      <button type="button" data-testid="build-submit-btn" onClick={submit} disabled={busy} className="group inline-flex w-full items-center justify-center gap-3 bg-ember px-7 py-4 font-semibold text-ink transition-[background-color,transform] duration-300 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60 sm:w-auto">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />} Request my build quote
        {!busy && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
      </button>
    </div>
  );
};
